<template>
  <div class="p-6 space-y-6">
    <!-- Header -->
    <div>
      <h1 class="text-2xl font-bold text-gray-800">📦 Gestion Stock {{ `${currentUser.type != 'Courier' ? 'Livreurs' : ''}` }} </h1>
      <p class="text-gray-500">Distribution des produits aux {{ `${currentUser.type != 'Courier' ? 'livreurs' : currentUser.fullName || currentUser.email  }` }} </p>
    </div>

    <!-- Filters -->
    <div  class="bg-white p-4 rounded-lg shadow">
      <div  class="grid grid-cols-1  gap-4"
       :class="[hasAllPermission() ? 'md:grid-cols-3' : 'md:grid-cols-1',(loadingFilter || loading) ? 'pointer-events-none opacity-50' : '']"
       >
        <div v-if="hasAllPermission()">
          <label class="text-sm text-gray-600 block mb-2">Rechercher Livreur</label>
          <v-select 
            label="fullName"
             @input="CheckFilter($event,'Courier')"
             placeholder="Nom ou email du livreur..."
             @search="searchCouriers"
             v-model="searchCourier"
              class="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
             :options="livreurs">
          </v-select>   
         
        </div>
        <div>
          <label class="text-sm text-gray-600 block mb-2">Filtre Produit</label>
          <v-select 
            label="name"
             @input="CheckFilter($event,'Product')"
             placeholder="ID ou nom du produit..."
             @search="searchProducts"
             v-model="searchProduct"
             class="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
             :options="products">
          </v-select>   

         
        </div>
        <div v-if="hasAllPermission()">
          <label class="text-sm text-gray-600 block mb-2">Ville</label>
          <v-select 
             placeholder="Ville ..."
             @input="CheckFilter($event,'City')"
             v-model="selectedCity"
             class="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
             :options="cities">
          </v-select>   
        </div>
      </div>
    </div>

    <!-- Stats -->
    <div v-if="hasAllPermission()" class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 bg-white">
        <div class="text-2xl font-bold text-blue-600">{{ paginate.total }}</div>
        <p class="text-sm text-blue-500">Total Livreurs</p>
      </div>
      <div class="bg-green-50 border border-green-200 rounded-lg p-4 bg-white">
        <div class="text-2xl font-bold text-green-600">{{ productWithCourier }}</div>
        <p class="text-sm text-green-500">Produits Distribués</p>
      </div>
      <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 bg-white">
        <div class="text-2xl font-bold text-yellow-600">{{ productNoCourier }}</div>
        <p class="text-sm text-yellow-500">Produits Disponibles</p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-8">
      <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
      <p class="text-gray-600 mt-2">Chargement...</p>
    </div>

    <!-- Couriers -->
    <div v-else class="space-y-6">
      <div
        v-for="courier in filteredCouriers"
        :key="courier._id"
        class="bg-white rounded-lg shadow border"
      >
        <!-- Header -->
        <div class="p-4 bg-gray-50 border-b flex justify-between items-center">
          <div class="flex items-center space-x-3">
            <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center font-medium text-blue-600">
              {{ courier.fullName ? courier.fullName.charAt(0) : 'L' }}
            </div>
            <div>
              <h3 class="font-semibold text-gray-900">{{ courier.fullName || 'Livreur' }}</h3>
              <p class="text-xs text-gray-500">{{ courier.email }}</p>
              <p class="text-xs text-gray-500">{{ courier.phone }}</p>
              <!-- 🔹 Horaire de travail -->
              <p v-if="courier.workingTime" class="text-xs font-medium mt-1 flex items-center gap-1">
                <span>⏰</span>
                <span class="px-1 py-0.5 rounded-full"
                      :class="workingTimeColor(courier.workingTime)">
                  {{ formatWorkingTime(courier.workingTime) }}
                </span>
              </p>
              <!-- Liste des villes -->
              <div v-if="courier.cities && courier.cities.length" class="mt-2 cities">
                <span class="inline-block mr-1 text-xs text-gray-600">📍</span>
                <div class="inline-block">
                  <div class="flex flex-wrap gap-1.5">
                    <span
                      v-for="(city, index) in courier.cities"
                      :key="index"
                      :class="['px-2 py-0.5 rounded-full text-xs font-medium', cityColor(index)]"
                    >
                      {{ city }}
                    </span>
                </div>
                </div>
              </div>

            </div>
          </div>
          <div class="text-right">
            <p class="text-xs text-gray-500">Stock Total</p>
            <p class="font-semibold text-gray-800">{{ courier.check ? getCourierTotalStock(courier) : '...' }} produits</p>
          </div>
        </div>

        <!-- Products -->
        <div class="p-4">
          <div v-if="hasAllPermission()" class="flex justify-between items-center mb-3">
            <h4 class="font-medium text-gray-700">Stock Livreur</h4>
            <button
              @click="openAddProductModal(courier)"
              class="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-sm"
            >
              + Ajouter Produit
            </button>
          </div>

          <!-- Table -->
          <div class="overflow-x-auto">
            <table class="min-w-full text-sm divide-y divide-gray-200">
              <thead class="bg-gray-100 text-gray-600 uppercase text-xs">
                <tr>
                  <th class="px-3 py-2 text-left">Produit</th>
                  <th class="px-3 py-2 text-left">Stock</th>
                  <th class="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
            </table>

            <div class="max-h-64 overflow-y-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <tbody v-if="!courier.check">
                  <tr>
                    <td colspan="3" class="py-3 text-center">
                      <button
                        @click="getCourierProducts(courier)"
                        :disabled="courier.loading"
                        class="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm transition-colors"
                      >
                        <!-- Icon -->
                        <svg xmlns="http://www.w3.org/2000/svg" 
                            class="h-4 w-4" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor">
                          <path stroke-linecap="round" 
                                stroke-linejoin="round" 
                                stroke-width="2" 
                                d="M3 7h18M3 12h18M3 17h18" />
                        </svg>
                        <!-- Label -->
                        <span>Afficher les produits assignés</span>
                      </button>

                    </td>
                  </tr>
                </tbody>

                <tbody v-else>
                  <tr
                    v-for="stock in courier.courierStocks"
                    :key="stock._id"
                    class="hover:bg-gray-50"
                  >
                    <td class="px-3 py-3 flex items-center space-x-3">
                      <img
                        v-if="stock.product && stock.product.picture"
                        :src="stock.product.picture"
                        class="w-10 h-10 rounded-md object-cover"
                      />
                      <div v-else class="w-10 h-10 bg-gray-200 rounded-md flex items-center justify-center text-xs text-gray-400">IMG</div>
                      <div>
                        <div class="font-medium text-gray-900">{{ stock.product ? stock.product.name : 'N/A' }}</div>
                        <div class="text-xs text-gray-500">Code: {{ stock.product ? stock.product.id || 'N/A' : `N/A (${stock.product ? product._id : ''})`  }}</div>
                      </div>
                    </td>
                    <td class="px-3 py-3 text-gray-800">{{ stock.quantity }}</td>
                    <td class="px-3 py-3 text-right">
                      <div class="space-x-2">
                        <button
                         v-if="hasAllPermission()"
                          @click="openEditStockModal(courier, stock)"
                          class="bg-yellow-100 hover:bg-yellow-200 text-yellow-800 px-3 py-1 rounded text-xs"
                        >
                          Modifier
                        </button>
                        <button
                          v-if="hasAllPermission()"
                          @click="removeProductFromCourier(courier, stock)"
                          class="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-xs"
                        >
                          Retirer
                        </button>
                        <button 
                          @click="openProductAnalytics(courier, stock)"
                          class="bg-purple-100 hover:bg-purple-200 text-purple-800 px-3 py-1 rounded text-xs font-medium transition-colors"
                          title="Voir les analytics"
                        >
                          <svg class="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00-2-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                          </svg>
                          Analytics
                      </button>
                      </div>
                    </td>
                  </tr>
                </tbody>

                <tbody v-if="courier.courierStocks.length === 0 && courier.check">
                  <tr>
                    <td colspan="3" class="py-3 text-center text-gray-500">Aucun produit assigné</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="paginate.lastpage > 1" class="pt-4 flex justify-center">
        <div class="flex flex-col items-center my-12">
            <div-paginate  :paginate="paginate" @paginatation="paginatation" />
        </div>
      </div>
    </div>

      <!-- Add Product Modal -->
      <div v-if="showAddProductModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 mt-0-imp">
      <div class="relative  bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col w-[600px] max-w-[90%] max-h-screen ">

        <header class="flex items-center justify-between px-6 py-4 border-b bg-gray-300">
          <h3 class="text-xl font-semibold text-gray-900 mb-6">
             Ajout de stock en masse - {{ selectedCourier.fullName }}
          </h3>
        </header>
       
        
        <div class="space-y-6 relative flex-1 overflow-y-auto px-6 pt-0 py-6 space-y-5">
          <loading  :active.sync="selectedCourier.loading" :is-full-page="fullPage"></loading>
          <!-- Sélecteur de produits avec dropdown -->
          <div class="mt-0">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Sélectionner des produits ({{ availableProducts.length }} disponibles)
            </label>
            
            <div class="relative">
              <input
                ref="productSearchInput"
                v-model="productSearchTerm"
                @input="onSearchInput"
                @focus="showProductDropdown = true"
                type="text"
                placeholder="Rechercher un produit par nom ou code..."
                class="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />

              <!-- Dropdown des produits disponibles -->
              <div 
                v-if="showProductDropdown && filteredAvailableProducts.length > 0"
                class="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto"
              >
                <button
                  v-for="product in filteredAvailableProducts"
                  :key="product._id"
                  @click="selectProduct(product)"
                  type="button"
                  class="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left border-b border-gray-100 last:border-0"
                >
                  <img
                    v-if="product.picture"
                    :src="product.picture"
                    :alt="product.name"
                    class="w-10 h-10 rounded-md object-cover"
                  />
                  <div v-else class="w-10 h-10 bg-gray-200 rounded-md flex items-center justify-center text-xs text-gray-400">
                    IMG
                  </div>
                  <div class="flex-1">
                    <div class="font-medium text-gray-900">{{ product.name }}</div>
                    <div class="text-xs text-gray-500">Code: {{ product.id || `N/A (${product._id})` }}</div>
                  </div>
                  <span class="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                    Stock: {{ getProductAvailableStock(product) }}
                  </span>
                </button>
              </div>

              <!-- Message si aucun produit trouvé -->
              <div 
                v-if="showProductDropdown && productSearchTerm && filteredAvailableProducts.length === 0"
                class="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-center text-gray-500"
              >
                Aucun produit trouvé
              </div>
            </div>

            <!-- Produits sélectionnés (chips) -->
            <div v-if="selectedProducts.length > 0" class="flex flex-wrap gap-2 mt-4">
              <span
                v-for="product in selectedProducts"
                :key="product._id"
                class="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
              >
                {{ product.name }}
                <button
                  @click="removeFromSelection(product._id)"
                  type="button"
                  class="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                  title="Retirer de la sélection"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            </div>
          </div>

          <!-- Tableau des produits sélectionnés -->
          <div v-if="selectedProducts.length > 0" class="border border-gray-200 rounded-lg overflow-hidden">
            <table class="w-full">
              <thead class="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th class="px-6 py-3 text-left text-sm font-semibold text-gray-700">Produit</th>
                  <th class="px-6 py-3 text-center text-sm font-semibold text-gray-700">Quantité</th>
                  <th class="px-6 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr
                  v-for="product in selectedProducts"
                  :key="product._id"
                  class="hover:bg-gray-50 transition-colors"
                >
                  <!-- Produit -->
                  <td class="px-6 py-4">
                    <div class="flex items-center gap-3">
                      <img
                        v-if="product.picture"
                        :src="product.picture"
                        :alt="product.name"
                        class="w-12 h-12 rounded-md object-cover"
                      />
                      <div v-else class="w-12 h-12 bg-gray-200 rounded-md flex items-center justify-center text-xs text-gray-400">
                        IMG
                      </div>
                      <div>
                        <div class="font-medium text-gray-900">{{ product.name }}</div>
                        <div class="text-sm text-gray-500">Code: {{ product.id || `N/A (${product._id})` }}</div>
                        <div 
                          v-if="getCourierProductStock(product._id)"
                          class="text-xs text-green-600 font-medium mt-0.5"
                        >
                          Stock livreur actuel: {{ getCourierProductStock(product._id) }}
                        </div>
                        <div class="text-xs text-gray-500 mt-0.5">
                          Stock disponible: {{ getProductAvailableStock(product) }}
                        </div>
                      </div>
                    </div>
                  </td>

                  <!-- Quantité -->
                  <td class="px-6 py-4">
                    <input
                      v-model.number="productQuantities[product._id]"
                      @input="validateQuantity(product._id)"
                      type="number"
                      min="1"
                      :max="getProductAvailableStock(product)"
                      class="w-24 px-3 py-2 border border-gray-300 rounded-md text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div 
                      v-if="productQuantities[product._id] > getProductAvailableStock(product)"
                      class="text-xs text-red-500 mt-1 text-center"
                    >
                      Max: {{ getProductAvailableStock(product) }}
                    </div>
                  </td>

                  <!-- Actions -->
                  <td class="px-6 py-4">
                    <div class="flex justify-end gap-2">
                      <!-- Bouton Ajouter (toujours visible) -->
                      <button
                        @click="addSingleProduct(product)"
                        :disabled="!isProductQuantityValid(product._id)"
                        type="button"
                        class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Ajouter
                      </button>

                      <!-- Bouton Retirer (si produit n'existe pas) OU Supprimer (si existe) -->
                      <button
                        v-if="!getCourierProductStock(product._id)"
                        @click="removeFromSelection(product._id)"
                        type="button"
                        class="px-4 py-2 border-2 border-red-600 text-red-600 hover:bg-red-50 rounded-md font-medium transition-colors"
                      >
                        Retirer
                      </button>
                      <button
                        v-else
                        @click="removeProductFromCourierStock(product)"
                        type="button"
                        class="px-4 py-2 border-2 border-red-600 text-red-600 hover:bg-red-50 rounded-md font-medium transition-colors"
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Message si aucun produit sélectionné -->
          <div v-else class="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
            <div class="text-gray-400 mb-4">
              <svg class="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">Aucun produit sélectionné</h3>
            <p class="text-gray-600">Utilisez le sélecteur ci-dessus pour ajouter des produits</p>
          </div>
        </div>

        <!-- Boutons d'action globaux -->
        <footer class="flex justify-end items-center gap-3 border-t bg-gray-50 px-6 py-4">
          <button 
            @click="closeAddProductModal"
            type="button"
            class="px-6 py-3 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors"
          >
            Annuler
          </button>
          <button 
            v-if="selectedProducts.length > 0"
            @click="addAllProducts"
            :disabled="!canAddAllProducts"
            type="button"
            class="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Ajouter tout ({{ selectedProducts.length }})
          </button>
        </footer>
       
      </div>
       </div>

    <!-- Edit Stock Modal -->
    <div v-if="showEditStockModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 mt-0-imp">
      <div class="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <h3 class="text-lg font-medium text-gray-900 mb-4">
          Modifier Stock - {{ editingStock ? editingStock.product.name : '' }}
        </h3>
        
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Action</label>
            <select 
              v-model="editAction" 
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="add">Ajouter au stock</option>
              <option value="remove">Retirer du stock</option>
              <!-- <option value="set">Définir le stock</option> -->
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Quantité</label>
            <input 
              v-model.number="editQuantity" 
              type="number" 
              min="1" 
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
            <p class="text-sm text-gray-500 mt-1">
              Stock actuel: {{ editingStock.quantity || 0 }}
            </p>
          </div>
        </div>

        <div class="flex justify-end space-x-3 mt-6">
          <button 
            @click="closeEditStockModal"
            class="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
          >
            Annuler
          </button>
          <button 
            @click="updateCourierStock"
            :disabled="!editQuantity"
            class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Modifier
          </button>
        </div>
      </div>
    </div>


      <!-- Analytics Modal -->
    <div v-if="showAnalyticsModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 mt-0-imp">
      <div class="bg-white rounded-lg w-full max-w-6xl mx-4 my-4 max-h-screen overflow-y-auto">
        <!-- Modal Header -->
        <div class="sticky top-0 bg-white border-b border-gray-200 px-6 py-2 rounded-t-lg">
          <div class="flex justify-between items-center">
            <div>
              <h3 class="text-xl font-semibold text-gray-900">Analytics Produit</h3>
              <div class="text-sm text-gray-500 mt-1">
                {{ selectedAnalyticsStock.product.name }} - 
                {{ selectedAnalyticsCourier.fullName }}
              </div>
            </div>
            <button 
              @click="closeAnalyticsModal"
              class="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="analyticsLoading" class="p-8 text-center">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <p class="mt-2 text-gray-600">Chargement des analytics...</p>
        </div>

        <!-- Analytics Content -->
        <div v-else-if="analyticsData" class="p-6">
          <!-- Summary Cards -->
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div class="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-blue-100 text-sm">Stock Actuel</p>
                  <p class="text-2xl font-bold">{{ analyticsData.currentStock }}</p>
                </div>
                <div class="bg-blue-400 bg-opacity-50 rounded-full p-2">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                  </svg>
                </div>
              </div>
            </div>

            <div class="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-green-100 text-sm">Total Livré</p>
                  <p class="text-2xl font-bold">{{ analyticsData.totalDelivered }}</p>
                </div>
                <div class="bg-green-400 bg-opacity-50 rounded-full p-2">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
              </div>
            </div>

            <div class="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-4 text-white">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-yellow-100 text-sm">Total Retourné</p>
                  <p class="text-2xl font-bold">{{ analyticsData.totalReturned }}</p>
                </div>
                <div class="bg-yellow-400 bg-opacity-50 rounded-full p-2">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                  </svg>
                </div>
              </div>
            </div>

            <div class="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-purple-100 text-sm">Taux Livraison</p>
                  <p class="text-2xl font-bold">{{ analyticsData.deliveryRate }}%</p>
                </div>
                <div class="bg-purple-400 bg-opacity-50 rounded-full p-2">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00-2-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <!-- Charts Section -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <!-- Movement Types Chart -->
            <div class="bg-white border border-gray-200 rounded-lg p-6">
              <h4 class="text-lg font-semibold text-gray-900 mb-4">Types de Mouvements</h4>
              <div class="space-y-3">
                <div v-for="type in analyticsData.movementTypes" :key="type.name" class="flex items-center justify-between">
                  <div class="flex items-center">
                    <div :class="['w-4 h-4 rounded-full mr-3', getMovementTypeColor(type.name)]"></div>
                    <span class="text-sm font-medium text-gray-700">{{ getMovementTypeLabel(type.name) }}</span>
                  </div>
                  <div class="flex items-center space-x-3">
                    <span class="text-sm text-gray-600">{{ type.count }} fois</span>
                    <div class="w-24 bg-gray-200 rounded-full h-2">
                      <div 
                        :class="['h-2 rounded-full', getMovementTypeColor(type.name)]"
                        :style="{ width: (type.count / analyticsData.maxMovementCount * 100) + '%' }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Stock Evolution Timeline -->
            <div class="bg-white border border-gray-200 rounded-lg p-6">
              <h4 class="text-lg font-semibold text-gray-900 mb-4">Évolution du Stock</h4>
              <div class="relative">
                <svg class="w-full h-40" viewBox="0 0 400 200">
                  <!-- Grid lines -->
                  <defs>
                    <pattern id="grid" width="40" height="20" patternUnits="userSpaceOnUse">
                      <path d="M 40 0 L 0 0 0 20" fill="none" stroke="#f1f5f9" stroke-width="1"/>
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />
                  
                  <!-- Stock evolution line -->
                  <polyline
                    :points="getStockEvolutionPoints()"
                    fill="none"
                    stroke="#8b5cf6"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  
                  <!-- Data points -->
                  <circle
                    v-for="(point, index) in analyticsData.stockEvolution"
                    :key="index"
                    :cx="(index / (analyticsData.stockEvolution.length - 1)) * 380 + 10"
                    :cy="190 - (point.quantity / analyticsData.maxStock * 180)"
                    r="3"
                    fill="#8b5cf6"
                  />
                </svg>
                <div class="mt-2 text-xs text-gray-500 text-center">
                  Évolution du stock au fil du temps
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Movements Table -->
          <div class="bg-white border border-gray-200 rounded-lg">
            <div class="px-6 py-4 border-b border-gray-200">
              <h4 class="text-lg font-semibold text-gray-900">Mouvements Récents</h4>
            </div>
            <div class="overflow-auto max-h-96" style="min-height: 200px;">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50 sticky top-0">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantité</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock Avant</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock Après</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Raison</th>
                    <th v-if="hasAllPermission()" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Par</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="movement in analyticsData.recentMovements" :key="movement._id">
                    <td class="px-6 py-2 text-sm text-gray-900">
                      {{ formatDate(movement.performedAt) }}
                    </td>
                    <td class="px-6 py-2">
                      <span :class="['inline-flex px-2 py-1 text-xs font-medium rounded-full', getMovementBadgeClass(movement.type)]">
                        {{ getMovementTypeLabel(movement.type) }}
                      </span>
                    </td>
                    <td class="px-6 py-2text-sm">
                      <span :class="getQuantityChangeClass(movement.type)">
                        {{ getQuantityChangeSymbol(movement.type) }}{{ movement.quantity }}
                      </span>
                    </td>
                    <td class="px-6 py-2 text-sm text-gray-900">{{ movement.previousQuantity }}</td>
                    <td class="px-6 py-2 text-sm text-gray-900">{{ movement.newQuantity }}</td>
                    <td class="px-6 py-2 text-sm text-gray-600">{{ movement.notes || movement.reason || '-' }}</td>
                    <td v-if="hasAllPermission()" class="px-6 py-2 text-sm text-gray-600">
                      {{ movement.performedBy ? movement.performedBy.fullName : 'Système' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object },
  },
  
  data() {
    return {
      loading: false,
      loadingFilter: false,
      fullPage: false,
      searchCourier: '',
      searchCourierss: [],
      searchProduct: '',
      searchProductss: [],
      selectedCity: '',
      limit: 1000,
      productWithCourier:0,
      productNoCourier:0,
      
      // Data
      couriers: [],
      courierStocks: [], // New collection to track stock per courier
      availableProducts: [],
      cities: [],
      products: [],
      livreurs: [],
      
      // Modals
      showAddProductModal: false,
      showAnalyticsModal: false,
      analyticsLoading: false,
      showEditStockModal: false,
      selectedCourier: null,
      selectedProduct: null,
      addQuantity: 1,

      // Nouvelles données pour la sélection multiple
      selectedProducts: [],
      productQuantities: {},
      productSearchTerm: '',
      showProductDropdown: false,
      
      // Edit stock
      editingStock: null,
      editingCourier: null,
      editAction: 'add',
      editQuantity: 1,
      filters:{},
      paginate: {
        total: 0,
        currentpage: 1,
        lastpage: 0,
        per_page: 0,
      },
    }
  },
  
  computed: {
    warhouseSelected() {
      return this.$store.getters["wharhouse/warhouseSelected"]({
        user: _.pick(this.currentUser, ['countries'])
      });
    },
    
    filteredCouriers() {
      let filtered = this.couriers;
      
      return filtered;
    },
    
    totalProductsDistributed() {
      return this.courierStocks.reduce((total, stock) => total + stock.quantity, 0);
    },
    
    totalStockValue() {
      return this.courierStocks.reduce((total, stock) => {
        return total + (stock.quantity * stock.product.price);
      }, 0).toFixed(2);
    },
     // Produits disponibles filtrés (exclus ceux déjà sélectionnés)
     availableProductsForSelection() {
      return this.availableProducts.filter(product => 
        !this.selectedProducts.find(sp => sp._id === product._id)
      );
    },
    
    // Produits filtrés par recherche
    filteredAvailableProducts() {
      if (!this.productSearchTerm.trim()) {
        return this.availableProductsForSelection;
      }
      
      const searchLower = this.productSearchTerm.toLowerCase();
      return this.availableProductsForSelection.filter(product =>
        product.name.toLowerCase().includes(searchLower) ||
        (product.id && product.id.toLowerCase().includes(searchLower))
      );
    },
    
    // Vérifier si on peut ajouter tous les produits
    canAddAllProducts() {
      if (this.selectedProducts.length === 0) return false;
      
      return this.selectedProducts.every(product => 
        this.isProductQuantityValid(product._id)
      );
    }
  },
  
  async mounted() {
    // Fermer le dropdown quand on clique ailleurs
    document.addEventListener('click', (e) => {
      if (this.$refs.productSearchInput && !this.$refs.productSearchInput.contains(e.target)) {
        this.showProductDropdown = false;
      }
    });
    if(this.$f.QueryStringToobject()) this.filters=this.$f.QueryStringToobject();
    else this.$f.objectToQueryString({ limit:this.limit,'countries':this.idWharhouse.country })

    await this.loadData();
  },
  
  watch: {
    'warhouseSelected': async function() {
     // await this.loadData();
    },
    
    'idWharhouse': async function(oldVal, newVal) {
       this.idWharhouse=oldVal;  
       this.$f.objectToQueryString({ limit:this.limit,'countries':this.idWharhouse.country })
       await this.loadData('updateCourierStock');
    }
  },
  
  methods: {
     hasAllPermission() {
       return this.$f.getAccessPermission(this.currentUser, 'courierstocks', 'create') &&
         this.$f.getAccessPermission(this.currentUser, 'courierstocks', 'update');
    },
    cityColor(index) {
      // Couleurs cycliques Tailwind (compatibles v1.8)
      const colors = [
        'bg-blue-100 text-blue-700',
        'bg-green-100 text-green-700',
        'bg-yellow-100 text-yellow-700',
        'bg-pink-100 text-pink-700',
        'bg-purple-100 text-purple-700',
        'bg-indigo-100 text-indigo-700',
        'bg-red-100 text-red-700',
        'bg-teal-100 text-teal-700'
      ];
      return colors[index % colors.length];
    },
    // Format du texte pour horaire
    formatWorkingTime(value) {
      if (value === 'matin') return this.$t('morning');       // Matin
      if (value === 'apres midi') return this.$t('afternoon'); // Après-midi
      return value;
    },

    // Couleur du badge horaire
    workingTimeColor(value) {
      if (value === 'matin') return 'bg-yellow-100 text-yellow-700';
      if (value === 'apres midi') return 'bg-orange-100 text-orange-700';
      return 'bg-gray-100 text-gray-600';
    },
    async loadData(nameAction = null) {
      this.loading = true;
      try {
        console.log('Loading data...',nameAction);
        if(this.couriers.length == 0 || nameAction) await this.getCouriers(this.filters)
      //  if(this.courierStocks.length == 0 || (nameAction && ['updateCourierStock','addProductToCourier'].includes(nameAction))) await this.getCourierStocks()
       if(this.currentUser.type !== 'Courier' && (this.availableProducts.length == 0 || nameAction)) await this.getAvailableProducts()
       if(this.cities.length == 0 || nameAction) await this.getZones();
       if(this.livreurs.length == 0 && this.currentUser.type !== 'Courier') await this.getLivreurs()
       if(this.products.length == 0) await this.getProducts()
        console.log('Available products loaded:', this.availableProducts.length);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        this.loading = false;
      }
    },
    
    async getCouriers(filters) {
      // const filter = {
      //   type: "Courier",
      //   status: "active",
      //   countries: this.idWharhouse.country,
      //   limit : this.limit
      // };

      filters.type = 'Courier';
      filters.status = 'active';
      filters.countries = this.idWharhouse.country;
      filters.limit = this.limit;

      const saved = localStorage.getItem("statsStockCourier");
      console.log('saved ----- ',saved)
      if (saved) {
        this.productWithCourier = JSON.parse(saved).productWithCourier;
        this.productNoCourier = JSON.parse(saved).productNoCourier;
      }else  filters.checkStatsProduct = true;
      
      const res = await this.$server.search("users",filters);
      console.log('getCouriers result ----- ',res)
      if (res && res.content.results) {
        this.couriers = res.content.results;
        this.paginate.total = res.content.total;
        this.paginate.lastpage = res.content.last_page;
        this.paginate.per_page = res.content.per_page;
        this.paginate.currentpage = res.content.current_page;
        if(res.content.withCourier || res.content.withoutCourier){
          this.productWithCourier = res.content.withCourier;
          this.productNoCourier = res.content.withoutCourier;
          localStorage.setItem("statsStockCourier", JSON.stringify({ productWithCourier: this.productWithCourier, productNoCourier: this.productNoCourier }));
        }
        if(this.currentUser.type == 'Courier'){
          await this.getCourierProducts(this.couriers[0]);
        }
       
      }else {
        this.couriers = [];this.paginate={total: 0,currentpage: 1,lastpage: 1,per_page: 0,}
      }
    },

    async paginatation(direction) {
      var page = 0;
      if (direction == "next") {
        page = ++this.paginate.currentpage;
      } else {
        page = --this.paginate.currentpage;
      }
      if(this.$f.QueryStringToobject()) this.filters=this.$f.QueryStringToobject();
      this.filters['page']=page;
      this.$f.objectToQueryString(this.filters)

      this.getCouriers(this.filters);
    },
    
    async getAvailableProducts() {
      try {
        console.log('Getting available products...');
        console.log('Warehouse ID:', this.idWharhouse);
        console.log('Selected warehouse:', this.idWharhouse.country);
        
        const filter = {
          'details.country': this.idWharhouse.country,
          'details.warehouse': this.idWharhouse._id,
          //'details.quantity.availible': { '$gt': 0 },
          courierstocks: true,
          limit : 20000
        };
        
        const res = await this.$server.search("products", filter);
        console.log('Products API response:', res);
        
        if (res.content && res.content.results) {
          console.log('Raw products:', res.content.results.length);
          
          // For debugging, let's see all products first
          let allProducts = res.content.results;
          console.log('Sample product structure:', allProducts[0]);
          
          // Filter products that have stock > 0 in the specific warehouse
          this.availableProducts = allProducts.filter(product => {
            // Check if product has details array
            if (!product.details || !Array.isArray(product.details)) {
              console.log('Product without details:', product.name);
              return false;
            }
            
            const warehouseDetail = product.details.find(d => {
              return d.warehouse && d.warehouse.toString() === this.idWharhouse._id.toString();
            });
            
            if (!warehouseDetail) {
              console.log('No warehouse detail for product:', product.name);
              return false;
            }
            
            // const hasStock = warehouseDetail.quantity && warehouseDetail.quantity.availible > 0;
            // console.log(`Product ${product.name}: stock = ${warehouseDetail.quantity.availible}`);

            const hasStock = warehouseDetail.quantity;
            
            return hasStock;
          });
          
          console.log('Filtered available products:', this.availableProducts.length);
        } else {
          console.log('No products found or API error');
          this.availableProducts = [];
        }
      } catch (error) {
        console.error('Error getting available products:', error);
        this.availableProducts = [];
      }
    },
    
    async getZones() {
      const filter = {
        country: this.idWharhouse.country
      };

      const resCities = await this.$server.get("zones", { allCities: true,country:this.idWharhouse.country}); 
      if(resCities && resCities.content.length>0)  this.cities= resCities.content;
      else this.cities=[];
    },
    
    async getCourierStocks(courier = null) {
      console.log('Getting courier stocks...');
      console.log('Courier:', courier);
      console.log('Product:', this.searchProduct);

      let filter = {
        warehouse: this.idWharhouse._id,
        country: this.idWharhouse.country,
        status: 'assigned',
        limit: 20000
      };

      let checkFiltrageCourier = false;

      if (courier) filter.courier = courier._id || courier;
      if (this.searchProduct && this.searchProduct._id) filter.product = this.searchProduct._id;
      if (this.searchCourier && this.searchCourier._id && !filter.courier){
        filter.courier = this.searchCourier._id;
        checkFiltrageCourier = true
      } 
      if (this.selectedCity) filter.cities = { $in : [this.selectedCity] };

      console.log('Courier stocks filter 11:', filter);

      try {

        if (courier && typeof courier === 'object') {
          courier.loading = true;
          courier.check = true;
        }else this.loading = true;

        console.log('Courier stocks filter 22:', filter);

        const res = await this.$server.search("courierstocks", filter);

        if (courier && typeof courier === 'object') courier.loading = false;
        else this.loading = false;

        const results = (res.content && res.content.results) ? res.content.results.filter(item => item.product)  : [];
        console.log('Courier stocks raw:', results);

        //check data not d=found by courier 
        console.log('searchCourier ======:', this.searchCourier);
        console.log('filter.courier ======:', filter.courier);
        console.log('searchProduct ======:', this.searchProduct);
        console.log('selectedCity ======:', this.selectedCity);
        console.log('results.length ======:', results.length);
        if (checkFiltrageCourier && !this.searchProduct && !this.selectedCity && results.length == 0){
           this.couriers = [this.searchCourier];
           this.$set(this.couriers[0], 'courierStocks', []);
           this.$set(this.couriers[0], 'check', false);
           console.log('couriers==========:', this.couriers);
        }else{
          // group by courier._id
          const grouped = results.reduce((acc, item) => {
              const cid = item && item.courier && item.courier._id
                ? item.courier._id.toString()
                : 'unknown';

                if (!acc[cid]) {
                  acc[cid] = {
                    courier: {
                      ...item.courier,
                      courierStocks: [],  // init vide
                      loading: false,     // init false
                      check: false        // init false
                    },
                    stocks: []
                  };
                }
              acc[cid].stocks.push(item);
              return acc;
            }, {});

            if(!courier){
              this.couriers = Object.values(grouped)
                .map(g => ({
                  ...g.courier,
                  courierStocks: g.stocks, // نربط كل courier بالستوك ديالو
                  loading: false,
                  check: false
                }));
            }

            console.log('Grouped courier stocks:', grouped);

            // If function called for a specific courier, set only that courier's stocks
            if (courier) {
              const cid = (courier._id ? courier._id.toString() : (courier + '')).toString();
              const stocks = grouped[cid] ? grouped[cid].stocks : [];
              // local object
              this.$set(courier, 'courierStocks', stocks);
              // also update in this.couriers if exists
              const idx = this.couriers.findIndex(c => (c._id && c._id.toString()) === cid);
              if (idx !== -1) {
                this.$set(this.couriers[idx], 'courierStocks', stocks);
                if (this.currentUser.type === 'Courier') this.$set(this.couriers[idx], 'check', true);
              }
              return; // done
            }

            console.log('Grouped courier stocks 22:', grouped);

            // No specific courier -> update all couriers in this.couriers
            this.couriers.forEach((c, i) => {
              const cid = c && c._id ? c._id.toString() : 'unknown';
              const stocks = grouped[cid] ? grouped[cid].stocks : [];
              this.$set(this.couriers[i], 'courierStocks', stocks);
              // optionally set check for current user type Courier
              this.$set(this.couriers[i], 'check', true);
            });

            // If you want a grouped object available globally:
            this.groupedCourierStocks = grouped; // optional, useful for later usage
        }

        console.log('couriers final:', this.couriers);

      } catch (error) {
        console.error('Error getting courier stocks:', error);

        // clear states safely
        if (courier) {
          this.$set(courier, 'courierStocks', []);
          courier.check = false;
          courier.loading = false;
        }
        // clear for all couriers
        this.couriers.forEach((c, i) => {
          this.$set(this.couriers[i], 'courierStocks', []);
          this.$set(this.couriers[i], 'check', false);
        });
      }
    },

    async CheckFilter(dataAction,action){
      console.log('dataAction----------',dataAction)
      console.log('action----------',action)

      if(action === 'Courier'){
         if(dataAction) this.searchCourier = dataAction;
         else this.searchCourier = null
      }

      if(action === 'Product'){
         if(dataAction) this.searchProduct = dataAction;
         else this.searchProduct = null
      }

      if(action === 'City'){
         if(dataAction) this.selectedCity = dataAction;
         else this.selectedCity = null
      }
      

      await this.getCourierStocks(this.currentUser.type === 'Courier' ? this.currentUser : null)

    },
    
    searchCouriers:  _.debounce(async function(name) {
      // Trigger reactive filtering
      console.log('searchCourier-----------',name)
      if (name.trim()) {
          const filter = {
            type: 'Courier',
            status: 'active',
            limit: 10,
            countries: this.idWharhouse.country,
            name: name.trim(),
        };

        this.loadingFilter = true;
        const res= await this.$server.find("users",filter);
        this.loadingFilter = false;

        if (res && res.content.results){
          this.livreurs = res.content.results;
        } else{
          this.livreurs = [];
        } 
      }
    }, 500),
    
    searchProducts: _.debounce(async function(name) {
      console.log('searchProducts-----------',name)
      if (name.trim()) {
        try {
          const filter = {
            name: name.trim(),
            limit: 10,
            'details.country': this.idWharhouse.country,
            ...(this.currentUser.type === 'Courier' ? {'details.courier': this.currentUser._id } : { 'details.courier': { $exists: true } }),
          };
          
          this.loadingFilter = true;
          const res = await this.$server.find("products", filter);
          this.loadingFilter = false;
          if (res.content && res.content.results) {
            this.products = res.content.results;
          } else {
            this.products = [];
          }
        } catch (error) {
          console.error('Error searching products:', error);
          this.products = [];
        }
      } 
    }, 500),

    async getProducts() {
      const filter = {
        limit: 10,
        'details.country': this.idWharhouse.country,
        ...(this.currentUser.type === 'Courier' ? {'details.courier': this.currentUser._id } : { 'details.courier': { $exists: true } }),
      };
      this.loadingFilter = true;
      const res = await this.$server.search("products", filter);
      this.loadingFilter = false;
      if (res.content.results) this.products = res.content.results;
      else this.products = [];
    },

    async getLivreurs() {
      const filter = {
        type: 'Courier',
        status: 'active',
        limit: 10,
        countries: this.idWharhouse.country,
      };
      this.loadingFilter = true;
      const res = await this.$server.search("users", filter);
      this.loadingFilter = false;
      if (res.content.results) this.livreurs = res.content.results;
      else this.livreurs = [];
    },
    
    async filterByCity(event) {
      // Trigger reactive filtering
      console.log('Filtering by city:', event.target.value);
      this.selectedCity = event.target.value;
      await this.getCourierStocks(this.currentUser.type === 'Courier' ? this.currentUser : null)
    },
    
   async getCourierProducts(courier) {
      await this.getCourierStocks(courier)
    },
    
    getCourierTotalStock(courier) {
      return courier.courierStocks.reduce((total, stock) => total + stock.quantity, 0);
    },
    
    getCourierStockValue(courier) {
      return this.courierStocks
        .reduce((total, stock) => total + (stock.quantity * stock.product.price), 0)
        .toFixed(2);
    },
    
    // Modal methods
    // openAddProductModal(courier) {
    //   this.selectedCourier = courier;
    //   this.selectedProduct = null;
    //   this.addQuantity = 1;
    //   this.showAddProductModal = true;
    // },
    
    // closeAddProductModal() {
    //   this.showAddProductModal = false;
    //   this.selectedCourier = null;
    //   this.selectedProduct = null; // Reset to null explicitly
    //   this.addQuantity = 1;
    //   console.log('Modal closed, selectedProduct reset to:', this.selectedProduct);
    // },
    
    openEditStockModal(courier, stock) {
      this.editingCourier = courier;
      this.editingStock = stock;
      this.editAction = 'add';
      this.editQuantity = 1;
      this.showEditStockModal = true;
    },
    
    closeEditStockModal() {
      this.showEditStockModal = false;
      this.editingCourier = null;
      this.editingStock = null;
    },
    
    async addProductToCourier() {
      if (!this.selectedProduct || !this.addQuantity) return;
      
      try {

        console.log('this.selectedCourier--------',this.selectedCourier)

        if(!this.selectedCourier.cities || this.selectedCourier.cities.length === 0) return this.$toast.warning('Veuillez ajouter des villes au livreur');
        if(!this.selectedCourier.workingTime) return this.$toast.warning('Veuillez ajouter des horaires de travail au livreur');
      
        let existingStock = null ;

        if(this.selectedCourier.courierStocks.length === 0){
          await this.getCourierStocks(this.selectedCourier);
        }

         // Check if courier already has this product
         existingStock = this.selectedCourier.courierStocks.find(stock => 
          stock.courier._id.toString() === this.selectedCourier._id.toString() && 
          stock.product._id.toString() === this.selectedProduct._id.toString()
        );

        console.log('selectedProduct----------',this.selectedProduct)

        
        if (existingStock) {
          // Update existing stock using PUT
          const updateData = {
            action: 'add',
            quantity: this.addQuantity,
            notes: 'Ajout de stock via interface'
          };
          
          await this.$server.update("courierstocks", {
            _id: existingStock._id,
            ...updateData
          });
        } else {
          // Create new stock entry using POST
          const newStock = {
            courier: this.selectedCourier._id,
            cities: this.selectedCourier.cities,
            product: this.selectedProduct._id,
            quantity: this.addQuantity,
            warehouse: this.idWharhouse._id,
            country: this.idWharhouse.country,
            notes: 'Assignation via interface'
          };
          
         const res = await this.$server.create("courierstocks", newStock);

         console.log('res create stock----------',res)

         if(res.status !== 200) return this.$toast.warning(res.content||res);

        }
        
        // Update product stock (you might need a separate endpoint for this)
        await this.updateProductStock(this.selectedProduct._id,this.addQuantity,'Subtraction',this.selectedCourier._id);
        
        // Refresh data
        await this.getCourierStocks(this.selectedCourier);
        this.closeAddProductModal();
        
        this.$toast.success('Produit ajouté avec succès');
        
      } catch (error) {
        console.error('Error adding product:', error);
        this.$toast.error('Erreur lors de l\'ajout du produit');
      }
    },
    
    async updateCourierStock() {
      if (!this.editQuantity) return;
      
      try {
        const updateData = {
          action: this.editAction,
          quantity: this.editQuantity,
          notes: `Modification ${this.editAction} via interface`
        };
        
       const res =  await this.$server.update("courierstocks", {
          _id: this.editingStock._id,
          ...updateData
        });

        console.log('updateCourierStock------',res)

        if(res.status === 200){
          await this.updateProductStock(this.editingStock.product._id||this.editingStock.product, this.editQuantity,this.editAction === 'add' ? 'Subtraction' : 'Addition',this.editingStock.courier._id||this.editingStock.courier);
        }else return this.$toast.warning(res.content||res);
        
        // Refresh data
        await this.getCourierStocks(this.editingStock.courier);
        this.closeEditStockModal();
        
        this.$toast.success('Stock modifié avec succès');
        
      } catch (error) {
        console.error('Error updating stock:', error);
        this.$toast.error('Erreur lors de la modification du stock');
      }
    },
    
    async removeProductFromCourier(courier, stock) {
      if (!confirm('Êtes-vous sûr de vouloir retirer ce produit du livreur?')) return;
      
      try {
       const resDe = await this.$server.delete("courierstocks", { 
          _id: stock._id,
          notes: 'Retrait via interface'
        });
        
        // Update product stock (return to main inventory)
       if(resDe && resDe.status === 200) await this.updateProductStock(stock.product._id, stock.quantity,'Addition',courier._id||courier);
       else return this.$toast.error(resDe.content);
        
        // Refresh data
        await this.getCourierStocks(courier);
        
        this.$toast.success('Produit retiré avec succès');
        
      } catch (error) {
        console.error('Error removing product:', error);
        this.$toast.error('Erreur lors du retrait du produit');
      }
    },
    
    // Helper methods for product data
    getProductAvailableStock(product) {
      if (!product || !product.details || !Array.isArray(product.details)) {
        console.log('Product missing details:', product);
        return 0;
      }
      
      if (!this.idWharhouse || !this.idWharhouse._id) {
        console.log('Warehouse ID missing');
        return 0;
      }
      
      const warehouseDetail = product.details.find(d => {
        return d.warehouse && d.warehouse.toString() === this.idWharhouse._id.toString();
      });
      
      if (!warehouseDetail) {
        console.log('No warehouse detail found for product:', product.name);
        return 0;
      }
      
      const stock = warehouseDetail.quantity.availible || 0;
      console.log(`Stock for ${product.name}:`, stock);
      return stock;
    },
    
    onProductSelect() {
      console.log('Product selected:', this.selectedProduct);
      
      // Reset quantity when product changes
      this.addQuantity = 1;
      
      // Validate max quantity
      if (this.selectedProduct) {
        const maxStock = this.getProductAvailableStock(this.selectedProduct);
        console.log('Max stock for selected product:', maxStock);
        if (this.addQuantity > maxStock) {
          this.addQuantity = maxStock;
        }
      }
    },
    
    async updateProductStock(productId, quantityChange,type, courier) {
      try {
        // Find the product in available products
        const product = this.availableProducts.find(p => p._id === productId);
        if (product) {
          // Find the warehouse detail for this specific warehouse
          const warehouseDetail = product.details.find(d => 
            d.warehouse === this.idWharhouse._id && 
            d.country === this.idWharhouse.country
          );
          
          if (warehouseDetail) {
            // Update the stock in the warehouse detail
            warehouseDetail.quantity.availible += quantityChange;
            
            // You might need to call a specific endpoint to update product stock
            // This depends on your backend API structure
            const updateData = {
              _id: productId,
              actionInstock: {
                operation: type,
                courier: courier,
                quantity : quantityChange,
                stock : 'inStock-total',
                warehouse: { _id: this.idWharhouse._id, country: this.idWharhouse.country },
              },
            };
            
            await this.$server.update("products", updateData);

            localStorage.removeItem("statsStockCourier");
          }
        }
      } catch (error) {
        console.error('Error updating product stock:', error);
      }
    },

     // Analytics Modal Methods
     async openProductAnalytics(courier, stock) {
      this.selectedAnalyticsCourier = courier;
      this.selectedAnalyticsStock = stock;
      this.showAnalyticsModal = true;
      this.analyticsLoading = true;
      
      try {
        await this.loadProductAnalytics(stock);
        this.analyticsLoading = false;
      } catch (error) {
        console.error('Error loading analytics:', error);
        this.$toast ? this.$toast.error('Erreur lors du chargement des analytics') : alert('Erreur lors du chargement');
      } finally {
        this.analyticsLoading = false;
      }
    },
    
    closeAnalyticsModal() {
      this.showAnalyticsModal = false;
      this.selectedAnalyticsCourier = null;
      this.selectedAnalyticsStock = null;
      this.analyticsData = null;
    },
    
    async loadProductAnalytics(stock) {
      try {
        // Get detailed stock with movements
        //const res = await this.$server.get("courierstocks", { _id: stock._id });
        
        // if (res.success && res.content) {
        //   const stockData = res.content;
        //   this.analyticsData = this.processAnalyticsData(stockData);
        // }

        this.analyticsData = this.processAnalyticsData(stock);

      } catch (error) {
        console.error('Error fetching stock analytics:', error);
        throw error;
      }
    },

    processAnalyticsData(stockData) {
      const movements = stockData.movements || [];
      
      // Calculate basic stats
      const currentStock = stockData.quantity || 0;
      const totalDelivered = movements
        .filter(m => m.type === 'delivered')
        .reduce((sum, m) => sum + m.quantity, 0);
      const totalReturned = movements
        .filter(m => m.type === 'returned')
        .reduce((sum, m) => sum + m.quantity, 0);
      
      // Calculate delivery rate
      const deliveryRate = totalDelivered > 0 ? 
        Math.round((totalDelivered / (totalDelivered + totalReturned)) * 100) : 0;
      
      // Movement types analysis
      const movementTypeCounts = {};
      movements.forEach(m => {
        movementTypeCounts[m.type] = (movementTypeCounts[m.type] || 0) + 1;
      });
      
      const movementTypes = Object.entries(movementTypeCounts).map(([type, count]) => ({
        name: type,
        count
      }));
      
      const maxMovementCount = Math.max(...Object.values(movementTypeCounts), 1);
      
      // Stock evolution timeline
      let stockEvolution = [];
      let runningStock = 0;
      
      movements.sort((a, b) => new Date(a.performedAt) - new Date(b.performedAt));
      
      movements.forEach((movement, index) => {
        stockEvolution.push({
          date: movement.performedAt,
          quantity: movement.newQuantity,
          type: movement.type,
          index
        });
      });
      
      const maxStock = Math.max(...stockEvolution.map(s => s.quantity), currentStock, 1);
      
      // Recent movements (last 10)
      const recentMovements = movements
        .sort((a, b) => new Date(b.performedAt) - new Date(a.performedAt))
        .slice(0, 10);
      
      return {
        currentStock,
        totalDelivered,
        totalReturned,
        deliveryRate,
        movementTypes,
        maxMovementCount,
        stockEvolution,
        maxStock,
        recentMovements
      };
    },
    
    // Analytics Helper Methods
    getMovementTypeLabel(type) {
      const labels = {
        'assigned': 'Assigné',
        'add': 'Ajout Stock',
        'remove': 'Retrait Stock',
        'set': 'Définir Stock',
        'delivered': 'Livré',
        'returned': 'Retourné'
      };
      return labels[type] || type;
    },
    
    getMovementTypeColor(type) {
      const colors = {
        'assigned': 'bg-blue-500',
        'add': 'bg-green-500',
        'remove': 'bg-yellow-500',
        'set': 'bg-purple-500',
        'delivered': 'bg-emerald-500',
        'returned': 'bg-red-500'
      };
      return colors[type] || 'bg-gray-500';
    },
    
    getMovementBadgeClass(type) {
      const classes = {
        'assigned': 'bg-blue-100 text-blue-800',
        'add': 'bg-green-100 text-green-800',
        'remove': 'bg-yellow-100 text-yellow-800',
        'set': 'bg-purple-100 text-purple-800',
        'delivered': 'bg-emerald-100 text-emerald-800',
        'returned': 'bg-red-100 text-red-800'
      };
      return classes[type] || 'bg-gray-100 text-gray-800';
    },
    
    getQuantityChangeClass(type) {
      if (['add', 'assigned', 'returned'].includes(type)) {
        return 'text-green-600 font-medium';
      } else if (['remove', 'delivered'].includes(type)) {
        return 'text-red-600 font-medium';
      }
      return 'text-purple-600 font-medium';
    },
    
    getQuantityChangeSymbol(type) {
      if (['add', 'assigned', 'returned'].includes(type)) {
        return '+';
      } else if (['remove', 'delivered'].includes(type)) {
        return '-';
      }
      return '=';
    },
    
    getStockEvolutionPoints() {
      if (!this.analyticsData || !this.analyticsData.stockEvolution || !this.analyticsData.stockEvolution.length) {
        return '';
      }

      
      return this.analyticsData.stockEvolution
        .map((point, index) => {
          const x = (index / (this.analyticsData.stockEvolution.length - 1)) * 380 + 10;
          const y = 190 - (point.quantity / this.analyticsData.maxStock * 180);
          return `${x},${y}`;
        })
        .join(' ');
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

     // ===== NOUVELLES MÉTHODES POUR LA SÉLECTION MULTIPLE =====
    
    // Ouvrir la modal (version améliorée)
   async openAddProductModal(courier) {
      this.selectedCourier = courier;
      this.selectedProducts = [];
      this.productQuantities = {};
      this.productSearchTerm = '';
      this.showProductDropdown = false;
      this.showAddProductModal = true;
      console.log('Modal opened, select courier', courier);
      await this.getCourierProducts(courier);
    },
    
    // Fermer la modal (version améliorée)
    closeAddProductModal() {
      this.showAddProductModal = false;
      this.selectedCourier = null;
      this.selectedProducts = [];
      this.productQuantities = {};
      this.productSearchTerm = '';
      this.showProductDropdown = false;
    },
    
    // Gérer l'input de recherche
    onSearchInput() {
      this.showProductDropdown = true;
    },
    
    // Sélectionner un produit
    selectProduct(product) {
      // Vérifier si le produit n'est pas déjà sélectionné
      if (this.selectedProducts.find(p => p._id === product._id)) {
        return;
      }
      
      // Ajouter le produit à la liste
      this.selectedProducts.push(product);
      
      // Initialiser la quantité à 1
      this.$set(this.productQuantities, product._id, 1);
      
      // Réinitialiser la recherche
      this.productSearchTerm = '';
      this.showProductDropdown = false;
    },
    
    // Retirer un produit de la sélection
    removeFromSelection(productId) {
      this.selectedProducts = this.selectedProducts.filter(p => p._id !== productId);
      this.$delete(this.productQuantities, productId);
    },
    
    // Valider la quantité d'un produit
    validateQuantity(productId) {
      const product = this.selectedProducts.find(p => p._id === productId);
      if (!product) return;
      
      const maxStock = this.getProductAvailableStock(product);
      const quantity = this.productQuantities[productId];
      
      if (quantity < 1) {
        this.$set(this.productQuantities, productId, 1);
      } else if (quantity > maxStock) {
        this.$set(this.productQuantities, productId, maxStock);
      }
    },
    
    // Vérifier si la quantité est valide
    isProductQuantityValid(productId) {
      const product = this.selectedProducts.find(p => p._id === productId);
      if (!product) return false;
      
      const quantity = this.productQuantities[productId];
      const maxStock = this.getProductAvailableStock(product);
      
      return quantity >= 1 && quantity <= maxStock;
    },
    
    // Obtenir le stock actuel du produit chez le livreur
    getCourierProductStock(productId) {
      console.log('selectedCourier----------',this.selectedCourier)
      console.log('productId----------',productId)
      if (!this.selectedCourier || !this.selectedCourier.courierStocks) {
        return 0;
      }
      
      const stock = this.selectedCourier.courierStocks.find(s => 
        s.product._id.toString() === productId.toString()
      );

      console.log('stock----------',stock)
      
      return stock ? stock.quantity : 0;
    },
    
    // Ajouter un seul produit
    async addSingleProduct(product) {
      if (!this.isProductQuantityValid(product._id)) {
        this.$toast.warning('Quantité invalide');
        return;
      }
      
      try {
        const quantity = this.productQuantities[product._id];
        
        // Vérifier les prérequis du livreur
        if (!this.selectedCourier.cities || this.selectedCourier.cities.length === 0) {
          return this.$toast.warning('Veuillez ajouter des villes au livreur');
        }
        if (!this.selectedCourier.workingTime) {
          return this.$toast.warning('Veuillez ajouter des horaires de travail au livreur');
        }
        
        // Charger le stock du livreur si nécessaire
        if (this.selectedCourier.courierStocks.length === 0) {
          await this.getCourierStocks(this.selectedCourier);
        }
        
        // Vérifier si le produit existe déjà
        const existingStock = this.selectedCourier.courierStocks.find(stock => 
          stock.courier._id.toString() === this.selectedCourier._id.toString() && 
          stock.product._id.toString() === product._id.toString()
        );
        
        if (existingStock) {
          // Mettre à jour le stock existant
          const updateData = {
            action: 'add',
            quantity: quantity,
            notes: 'Ajout de stock via interface (masse)'
          };
          
          await this.$server.update("courierstocks", {
            _id: existingStock._id,
            ...updateData
          });
        } else {
          // Créer une nouvelle entrée de stock
          const newStock = {
            courier: this.selectedCourier._id,
            cities: this.selectedCourier.cities,
            product: product._id,
            quantity: quantity,
            warehouse: this.idWharhouse._id,
            country: this.idWharhouse.country,
            notes: 'Assignation via interface (masse)'
          };
          
          const res = await this.$server.create("courierstocks", newStock);
          
          if (res.status !== 200) {
            return this.$toast.warning(res.content || res);
          }
        }
        
        // Mettre à jour le stock du produit
        await this.updateProductStock(product._id, quantity, 'Subtraction', this.selectedCourier._id);
        
        // Retirer le produit de la sélection
        this.removeFromSelection(product._id);
        
        // Rafraîchir les données
        await this.getCourierStocks(this.selectedCourier);
        await this.getAvailableProducts();
        
        this.$toast.success(`Produit "${product.name}" ajouté avec succès`);
        
      } catch (error) {
        console.error('Error adding single product:', error);
        this.$toast.error('Erreur lors de l\'ajout du produit');
      }
    },
    
    // Ajouter tous les produits sélectionnés
    async addAllProducts() {
      if (!this.canAddAllProducts) {
        this.$toast.warning('Veuillez corriger les quantités invalides');
        return;
      }
      
      const totalProducts = this.selectedProducts.length;
      let successCount = 0;
      let failCount = 0;
      
      this.$toast.info(`Ajout de ${totalProducts} produit(s) en cours...`);
      
      // Traiter chaque produit
      for (const product of this.selectedProducts) {
        try {
          const quantity = this.productQuantities[product._id];
          
          // Charger le stock du livreur si nécessaire
          if (this.selectedCourier.courierStocks.length === 0) {
            await this.getCourierStocks(this.selectedCourier);
          }
          
          // Vérifier si le produit existe déjà
          const existingStock = this.selectedCourier.courierStocks.find(stock => 
            stock.courier._id.toString() === this.selectedCourier._id.toString() && 
            stock.product._id.toString() === product._id.toString()
          );
          
          if (existingStock) {
            // Mettre à jour le stock existant
            await this.$server.update("courierstocks", {
              _id: existingStock._id,
              action: 'add',
              quantity: quantity,
              notes: 'Ajout de stock en masse'
            });
          } else {
            // Créer une nouvelle entrée de stock
            const newStock = {
              courier: this.selectedCourier._id,
              cities: this.selectedCourier.cities,
              product: product._id,
              quantity: quantity,
              warehouse: this.idWharhouse._id,
              country: this.idWharhouse.country,
              notes: 'Assignation en masse'
            };
            
            const res = await this.$server.create("courierstocks", newStock);
            
            if (res.status !== 200) {
              failCount++;
              continue;
            }
          }
          
          // Mettre à jour le stock du produit
          await this.updateProductStock(product._id, quantity, 'Subtraction', this.selectedCourier._id);
          
          successCount++;
          
        } catch (error) {
          console.error('Error adding product:', product.name, error);
          failCount++;
        }
      }
      
      // Rafraîchir les données
      await this.getCourierStocks(this.selectedCourier);
      await this.getAvailableProducts();
      
      // Fermer la modal
      this.closeAddProductModal();
      
      // Afficher le résultat
      if (successCount === totalProducts) {
        this.$toast.success(`Tous les produits ont été ajoutés avec succès (${successCount}/${totalProducts})`);
      } else if (successCount > 0) {
        this.$toast.warning(`${successCount} produit(s) ajouté(s), ${failCount} échec(s)`);
      } else {
        this.$toast.error('Échec de l\'ajout des produits');
      }
    },
    
    // Supprimer un produit du stock du livreur (bouton "Supprimer")
    async removeProductFromCourierStock(product) {
      const currentStock = this.getCourierProductStock(product._id);
      const quantity = this.productQuantities[product._id];

      if (quantity > currentStock) {
        this.$toast.warning('La quantité que vous voulez supprimer est supérieure au stock livreur actuel.');
        return;
      }
      
      if (!confirm(`⚠️ Voulez-vous vraiment retirer ${quantity} unité(s) de "${product.name}" du stock du livreur ?\n(Stock actuel: ${currentStock})`)) {
        return;
      }
      
      try {
        // Trouver le stock existant
        const existingStock = this.selectedCourier.courierStocks.find(stock => 
          stock.product._id.toString() === product._id.toString()
        );
        
        if (!existingStock) {
          this.$toast.warning('Stock non trouvé');
          return;
        }
        
        // Retirer le stock
        await this.$server.update("courierstocks", {
          _id: existingStock._id,
          action: 'remove',
          quantity: quantity,
          notes: 'Retrait via interface (masse)'
        });
        
        // Mettre à jour le stock du produit (rajouter au warehouse)
        await this.updateProductStock(product._id, quantity, 'Addition', this.selectedCourier._id);
        
        // Retirer de la sélection
        this.removeFromSelection(product._id);
        
        // Rafraîchir les données
        await this.getCourierStocks(this.selectedCourier);
        await this.getAvailableProducts();
        
        this.$toast.success(`${quantity} unité(s) de "${product.name}" retirée(s) du stock`);
        
      } catch (error) {
        console.error('Error removing product from courier:', error);
        this.$toast.error('Erreur lors du retrait du produit');
      }
    },
    
    // Méthode utilitaire existante à garder
    getProductAvailableStock(product) {
      if (!product || !product.details || !Array.isArray(product.details)) {
        return 0;
      }
      
      const warehouseDetail = product.details.find(d => 
        d.warehouse && d.warehouse.toString() === this.idWharhouse._id.toString()
      );
      
      return warehouseDetail && warehouseDetail.quantity ? warehouseDetail.quantity.availible : 0;
    },
   
    
  }
}
</script>
<style scoped>
.max-h-64 { max-height: 16rem } /* tailwind */
.overflow-y-auto { overflow-y: auto }
.text-green-600, .text-green-500 {
  color: #38a169;
}
/* Tailwind: bg-emerald-500 */
/* bg-emerald-500 */
.bg-emerald-500 {
  --tw-bg-opacity: 1;
  background-color: rgb(16 185 129 / var(--tw-bg-opacity)); /* #10b981 */
}

/* bg-emerald-100 */
.bg-emerald-100 {
  --tw-bg-opacity: 1;
  background-color: rgb(209 250 229 / var(--tw-bg-opacity)); /* #d1fae5 */
}

/* text-emerald-800 */
.text-emerald-800 {
  --tw-text-opacity: 1;
  color: rgb(6 95 70 / var(--tw-text-opacity)); /* #065f46 */
}
.max-h-96 {
  max-height: 24rem; /* 384px */
}
.mt-0-imp{
  margin-top: 0 !important;
}

.cities span {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.cities span:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}


.max-h-screen { max-height: 75vh;width: 75%;
  max-width: 90%;
  box-sizing: border-box; }
/* Styles pour le dropdown */
.z-10 { z-index: 10; }

</style>